/**
 * Phase 66 Plan 06 Task 1 — Topbar tenant switcher (D-13).
 *
 * Renders a dropdown listing the circuits the current user can scope
 * queries by. super_admin gets a leading "Tutti i circuiti" entry that
 * sets circuit_id to null.
 *
 * On select:
 *   1. setCircuitId(id) — updates URL ?circuit_id + localStorage
 *   2. queryClient.invalidateQueries() — refetches everything keyed by tenant
 *      (defense-in-depth per Pitfall 8 / T-66-06-01)
 *
 * The trigger label is the currently-selected circuit's name when known,
 * else "Tutti i circuiti" (super_admin global) or "—" (no selection).
 *
 * Backend permission boundary (T-66-06-02): GET /admin/circuits is
 * filtered server-side by JWT scope. A bookmark-injected ?circuit_id
 * pointing at a circuit the user cannot see results in 403 from the
 * downstream endpoints — handled globally by api/client.ts.
 */

import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Building2, Check, ChevronDown } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { listCircuits } from "@/api/circuits";
import { qk } from "@/lib/queryKeys";
import { useCircuitId, useSetCircuitId } from "@/hooks/useCircuitId";
import { useAuth } from "@/auth/AuthProvider";

export default function TenantSwitcher() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const currentId = useCircuitId();
  const setCircuitId = useSetCircuitId();

  const isSuperAdmin = user?.role === "super_admin";

  const { data: circuits = [], isLoading } = useQuery({
    queryKey: qk.circuits(),
    queryFn: listCircuits,
    staleTime: 60_000,
    // Fail silently — the dropdown stays usable with the cached entry.
    retry: false,
  });

  if (!user) return null;

  const current = circuits.find((c) => c.id === currentId);
  const triggerLabel = current
    ? current.name
    : isSuperAdmin && !currentId
      ? t("tenant.allCircuits")
      : currentId ?? t("tenant.placeholder");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="tenant-switcher"
          className="tenant-switcher inline-flex items-center gap-2 px-3 py-1.5 rounded border border-[color:var(--color-border)] bg-[color:var(--color-surface)] text-sm hover:bg-[color:var(--color-bg)] cursor-pointer"
          aria-label={t("tenant.switchTo")}
        >
          <Building2 size={14} aria-hidden="true" />
          <span className="max-w-[160px] truncate">{triggerLabel}</span>
          <ChevronDown size={14} aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px]">
        <DropdownMenuLabel>{t("tenant.switchTo")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {isSuperAdmin && (
          <DropdownMenuItem
            data-testid="tenant-switcher-item-all"
            onSelect={(e) => {
              e.preventDefault();
              setCircuitId(null);
            }}
            className="cursor-pointer"
          >
            <Check
              size={14}
              className={`mr-2 ${currentId ? "opacity-0" : "opacity-100"}`}
              aria-hidden="true"
            />
            {t("tenant.allCircuits")}
          </DropdownMenuItem>
        )}
        {isLoading && (
          <DropdownMenuItem disabled>{t("common.loading")}</DropdownMenuItem>
        )}
        {circuits.map((c) => (
          <DropdownMenuItem
            key={c.id}
            data-testid={`tenant-switcher-item-${c.id}`}
            onSelect={(e) => {
              e.preventDefault();
              setCircuitId(c.id);
            }}
            className="cursor-pointer"
          >
            <Check
              size={14}
              className={`mr-2 ${currentId === c.id ? "opacity-100" : "opacity-0"}`}
              aria-hidden="true"
            />
            <span className="truncate">{c.name}</span>
            <span className="ml-auto text-xs text-[color:var(--color-text-muted)] tabular-nums">
              {c.id}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
